import type { ThemeConfig } from "@/types/theme";

export const tailwindPink: ThemeConfig = {
  classes: {
    // Color classes
    primary: "text-pink-700 dark:text-pink-400",
    secondary: "text-pink-900 dark:text-pink-500",
    accent: "text-rose-600 dark:text-rose-400",

    // Background classes
    backgroundPrimary: "bg-white dark:bg-gray-950",
    backgroundSecondary: "bg-gray-50 dark:bg-gray-900",
    backgroundSurface: "bg-gray-50 dark:bg-gray-800",
    backgroundTertiary: "bg-pink-50/60 dark:bg-gray-800",
    backgroundAccent: "bg-pink-600 dark:bg-pink-700",
    backgroundPrimaryHover: "hover:bg-gray-50 dark:hover:bg-gray-900",
    decorativeBackground:
      "bg-gradient-to-br from-pink-50 to-rose-50 dark:from-gray-900 dark:to-gray-950",

    // Shared alternates (same across palettes)
    backgroundSoft: "bg-gray-50 dark:bg-gray-900",
    backgroundLighter: "bg-white dark:bg-gray-900",

    backgroundPattern: "bg-pink-100 dark:bg-gray-800",
    backgroundPrimaryColor: "bg-pink-600 dark:bg-pink-500",
    backgroundSecondaryColor: "bg-pink-800 dark:bg-pink-700",
    backgroundAccentColor: "bg-rose-500 dark:bg-rose-400",
    backgroundPrimarySubtle: "bg-pink-50 dark:bg-pink-950",

    // Card backgrounds
    backgroundCard: "bg-white dark:bg-gray-950",
    backgroundElevated: "bg-white dark:bg-gray-800",
    backgroundHighlighted:
      "bg-gradient-to-br from-white to-pink-50 dark:from-gray-800 dark:to-gray-900",

    // Text classes
    textPrimary: "text-gray-800 dark:text-gray-100",
    textSecondary: "text-gray-600 dark:text-gray-300",
    textMuted: "text-gray-500 dark:text-gray-400",
    textInverse: "text-white dark:text-gray-900",
    textAccent: "text-pink-700 dark:text-pink-400",
    textOnAccent: "text-white",

    // Text on dark backgrounds
    textPrimaryOnDark: "text-gray-100",
    textSecondaryOnDark: "text-gray-300",
    textMutedOnDark: "text-gray-400",

    // Icon classes
    iconText: "text-pink-600 dark:text-pink-400",
    iconBackgroundFilled: "bg-pink-100 dark:bg-pink-950",
    iconBackgroundGradient: "bg-gradient-to-br from-pink-500 to-rose-500",
    iconBackgroundDefault: "bg-pink-50 dark:bg-pink-950/50",
    iconBorder: "border-pink-200 dark:border-pink-800",

    // Border classes
    borderDefault: "border-pink-200 dark:border-gray-700",
    borderLight: "border-pink-100 dark:border-gray-800",
    borderStrong: "border-pink-300 dark:border-gray-600",
    borderPrimary: "border-pink-700 dark:border-pink-500",
    borderSecondary: "border-pink-900 dark:border-pink-700",
    borderBottom: "border-b border-pink-200 dark:border-gray-700",
    borderAccent: "border-pink-600 dark:border-pink-400",

    // Badge classes
    badgeBackground: "bg-pink-100 dark:bg-pink-950",
    badgeText: "text-pink-700 dark:text-pink-300",

    // Shadow classes
    shadowPrimary: "shadow-pink-500/20",
    shadowDefault: "shadow-sm",
    shadowHover: "hover:shadow-lg hover:shadow-pink-500/10",
    shadowSmall: "shadow-sm",
    shadowMedium: "shadow-md",
    shadowLarge: "shadow-lg",
    shadowXLarge: "shadow-xl",
    shadowGlow: "shadow-[0_0_30px_rgba(236,72,153,0.30)]", // pink-500 glow
    shadowInset: "shadow-inner",

    // Interactive states
    hoverBackground: "hover:bg-pink-50 dark:hover:bg-gray-800",
    activeBackground: "active:bg-pink-100 dark:active:bg-gray-700",
    accentPrimary:
      "bg-pink-600 hover:bg-pink-700 dark:bg-pink-700 dark:hover:bg-pink-600",

    // Creative Bold Hero classes
    backgroundBold: "bg-pink-400 dark:bg-pink-600",
    textEmphasis: "text-cyan-600 dark:text-cyan-400",

    // Gradient classes
    gradientPrimary: "bg-gradient-to-r from-pink-600 to-pink-700",
    gradientSecondary: "bg-gradient-to-br from-pink-700 to-pink-900",
    gradientAccent: "bg-gradient-to-r from-pink-500 to-rose-500",
    gradientSubtle:
      "bg-gradient-to-br from-pink-50 to-rose-50 dark:from-gray-900 dark:to-gray-950",
    gradientVibrant: "bg-gradient-to-r from-pink-600 via-rose-500 to-pink-600",
    gradientHero: "bg-gradient-to-br from-pink-600 via-rose-500 to-pink-700",

    // Border radius classes
    roundedSmall: "rounded",
    roundedMedium: "rounded-md",
    roundedLarge: "rounded-lg",
    roundedXLarge: "rounded-xl",
    roundedFull: "rounded-full",

    // Opacity classes
    opacityDisabled: "opacity-50",
    opacityHover: "opacity-80",
    opacityBackdrop: "opacity-60",
    opacitySubtle: "opacity-30",

    // Transition classes
    transitionFast: "transition-all duration-150",
    transitionNormal: "transition-all duration-300",
    transitionSlow: "transition-all duration-500",

    // Z-index classes
    zDropdown: "z-10",
    zModal: "z-50",
    zOverlay: "z-40",
    zTooltip: "z-[60]",
    zFixed: "z-30",

    // Status colors (kept consistent across palettes)
    error: "text-red-600 dark:text-red-400",
    success: "text-green-600 dark:text-green-400",
    warning: "text-amber-600 dark:text-amber-400",
    info: "text-blue-600 dark:text-blue-400",

    errorBg: "bg-red-100 dark:bg-red-950",
    successBg: "bg-green-100 dark:bg-green-950",
    warningBg: "bg-amber-100 dark:bg-amber-950",
    infoBg: "bg-blue-100 dark:bg-blue-950",
  },
};
